import { useMemo, useState } from "react";
import { PageHeader } from "../shell/PageHeader";
import {
  useGetV1Assets,
  type EnvelopeAssetListDataItemsItem,
} from "@solarkey/api-client";
import {
  DataTable,
  EmptyState,
  ErrorState,
  Skeleton,
  type ColumnDef,
} from "@solarkey/ui";

type Row = EnvelopeAssetListDataItemsItem;
type Tab = "registry" | "meter_groups" | "block_membership";

/* The registry read states which columns it cannot fill (and why) in `withheld`,
   so a column with no source anywhere in scope is dropped with a reason instead of
   printing an em dash on every row. Plain sentences for the templates it uses. */
const REASON_TEXT: Record<string, string> = {
  NOT_MEASURED_BLOCK_LABEL_NOT_AUTHORED:
    "No block has been authored against any device in scope, so block membership cannot be shown.",
  NOT_MEASURED_METER_GROUPS_NOT_AUTHORED:
    "No meter-to-inverter mapping is recorded, so meter groups cannot be shown.",
  NOT_MEASURED_NO_TOPOLOGY_EDGES:
    "Connectivity between devices is not recorded yet.",
};

interface Withheld {
  [key: string]: { template?: string } | undefined;
}

function reasonFor(withheld: Withheld, key: string): string | null {
  const t = withheld[key]?.template;
  if (!t) return null;
  return REASON_TEXT[t] ?? t.toLowerCase().replace(/_/g, " ");
}

export function Assets(): React.ReactNode {
  const [tab, setTab] = useState<Tab>("registry");
  const query = useGetV1Assets();

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-density-pad" aria-busy="true">
        <Skeleton variant="text" width="10rem" />
        <Skeleton variant="row" lines={6} />
      </div>
    );
  }

  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <ErrorState
        title="Assets unavailable"
        message="The registry could not be loaded."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const data = query.data.data.data;
  const rows = data.items ?? [];
  const withheld = ((data as unknown as { withheld?: Withheld }).withheld ?? {}) as Withheld;

  return (
    <div className="flex flex-col gap-density-pad">
      <PageHeader title="Assets" scope="Registry & topology — as recorded, with make, model and rating." showTimeControl={false} />

      <div className="flex gap-1" role="tablist" aria-label="Asset views">
        {([
          ["registry", "Registry"],
          ["meter_groups", "Meter groups"],
          ["block_membership", "Block membership"],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`rounded-t border px-density-pad py-2 text-sm ${
              tab === key
                ? "border-b-transparent bg-surface font-semibold text-accent"
                : "border-transparent text-text-muted hover:text-text"
            }`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "registry" ? <RegistryTable rows={rows} withheld={withheld} /> : null}
      {tab === "meter_groups" ? <MeterGroupsView rows={rows} withheld={withheld} /> : null}
      {tab === "block_membership" ? <BlockMembershipView rows={rows} withheld={withheld} /> : null}
    </div>
  );
}

/** A cell with no recorded value: the state word, never a bare dash or a zero. */
function NotRecorded({ what }: { what: string }): React.ReactNode {
  return (
    <span className="sk-stateword" title={`Not recorded in the registry: ${what}.`}>
      Not recorded
    </span>
  );
}

/** The one nameplate rating the registry holds for a device, with its unit. */
function ratingOf(r: Row): { value: number; unit: string } | null {
  const ratings = r.ratings ?? {};
  for (const [key, unit] of [
    ["rated_kw", "kW"],
    ["rated_kva", "kVA"],
    ["rated_kwh", "kWh"],
    ["rated_dc_kwp", "kWp"],
  ] as const) {
    const v = ratings[key];
    if (typeof v === "number") return { value: v, unit };
  }
  return null;
}

function RegistryTable({ rows, withheld }: { rows: Row[]; withheld: Withheld }): React.ReactNode {
  // Nothing writes a block label yet and the meter map may be unauthored; the read
  // says so, and the column is dropped rather than shown empty on every row.
  const blockReason = reasonFor(withheld, "block_id");
  const meterReason = reasonFor(withheld, "meter_group_id");

  const columns: ColumnDef<Row, unknown>[] = [
    { id: "role_name", header: "Role", cell: (c) => c.row.original.role_name ?? c.row.original.id ?? <NotRecorded what="role name" /> },
    { id: "device_class", header: "Class", cell: (c) => c.row.original.device_class ?? <NotRecorded what="device class" /> },
    { id: "make", header: "Make", cell: (c) => c.row.original.make ?? <NotRecorded what="make" /> },
    { id: "model", header: "Model", cell: (c) => c.row.original.model ?? <NotRecorded what="model" /> },
    {
      id: "rating",
      header: "Rating",
      cell: (c) => {
        const r = ratingOf(c.row.original);
        return r ? `${r.value.toLocaleString()} ${r.unit}` : <NotRecorded what="nameplate rating" />;
      },
    },
    ...(blockReason
      ? []
      : [{
          id: "block_id",
          header: "Block",
          cell: (c) => c.row.original.block_id ?? <NotRecorded what="block" />,
        } as ColumnDef<Row, unknown>]),
    ...(meterReason
      ? []
      : [{
          id: "meter_group",
          header: "Meter group",
          cell: (c) => c.row.original.meter_group_id ?? <NotRecorded what="meter group" />,
        } as ColumnDef<Row, unknown>]),
    { id: "site_id", header: "Site", cell: (c) => c.row.original.site_id ?? <NotRecorded what="site" /> },
  ];

  return (
    <div className="flex flex-col gap-density-pad">
      <DataTable<Row>
        columns={columns}
        rows={rows}
        getRowId={(r, i) => r.id ?? String(i)}
        emptyState={<EmptyState variant="no_data" title="No assets in scope" />}
      />
      {blockReason || meterReason ? (
        <p className="text-sm text-text-muted">
          Columns not shown:{" "}
          {[blockReason ? `Block — ${blockReason}` : null, meterReason ? `Meter group — ${meterReason}` : null]
            .filter(Boolean)
            .join(" ")}
        </p>
      ) : null}
    </div>
  );
}

function MeterGroupsView({ rows, withheld }: { rows: Row[]; withheld: Withheld }): React.ReactNode {
  const reason = reasonFor(withheld, "meter_group_id");

  const groups = useMemo(() => {
    const map = new Map<string, Row[]>();
    for (const r of rows) {
      const gid = r.meter_group_id;
      if (!gid) continue;
      const arr = map.get(String(gid)) ?? [];
      arr.push(r);
      map.set(String(gid), arr);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  // No mapping authored: say that, rather than sweeping every device into a
  // fabricated "ungrouped" group.
  if (reason) {
    return <EmptyState variant="no_data" title="Meter groups are not recorded" message={reason} />;
  }
  if (groups.length === 0) {
    return <EmptyState variant="no_data" title="No meter groups in scope" />;
  }

  const ungrouped = rows.filter((r) => !r.meter_group_id);

  return (
    <div className="flex flex-col gap-density-pad">
      {groups.map(([gid, members]) => (
        <section key={gid} className="rounded border border-border p-density-pad">
          <h3 className="mb-2 font-medium">
            {gid}
            <span className="ml-2 text-sm text-text-muted">{members.length} {members.length === 1 ? "asset" : "assets"}</span>
          </h3>
          <ul className="flex flex-col gap-1" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {members.map((m, i) => (
              <li key={m.id ?? i} className="flex items-center justify-between border-b border-border py-1 text-sm">
                <span>{m.role_name ?? m.id ?? ""}</span>
                <span className="text-text-muted">{m.device_class ?? ""}{m.make ? ` · ${m.make}` : ""}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {ungrouped.length > 0 ? (
        <p className="text-sm text-text-muted">
          {ungrouped.length} {ungrouped.length === 1 ? "device is" : "devices are"} behind no
          recorded meter. That is a gap in the mapping, not a group.
        </p>
      ) : null}
    </div>
  );
}

function BlockMembershipView({ rows, withheld }: { rows: Row[]; withheld: Withheld }): React.ReactNode {
  const reason = reasonFor(withheld, "block_id");

  const blocks = useMemo(() => {
    const map = new Map<string, Row[]>();
    for (const r of rows) {
      const bid = r.block_id;
      if (!bid) continue;
      const arr = map.get(String(bid)) ?? [];
      arr.push(r);
      map.set(String(bid), arr);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  // Nothing writes `block_label`, so an "unassigned" bucket holding every device
  // would assert a membership answer the registry does not have.
  if (reason) {
    return <EmptyState variant="no_data" title="Block membership is not recorded" message={reason} />;
  }
  if (blocks.length === 0) {
    return <EmptyState variant="no_data" title="No blocks in scope" />;
  }

  const unassigned = rows.filter((r) => !r.block_id);

  return (
    <div className="flex flex-col gap-density-pad">
      {blocks.map(([bid, members]) => (
        <section key={bid} className="rounded border border-border p-density-pad">
          <h3 className="mb-2 font-medium">
            {bid}
            <span className="ml-2 text-sm text-text-muted">{members.length} {members.length === 1 ? "device" : "devices"}</span>
          </h3>
          <ul className="flex flex-col gap-1" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {members.map((m, i) => (
              <li key={m.id ?? i} className="flex items-center justify-between border-b border-border py-1 text-sm">
                <span>{m.role_name ?? m.id ?? ""}</span>
                <span className="text-text-muted">
                  {m.device_class ?? ""}
                  {m.meter_group_id ? ` · meter group: ${m.meter_group_id}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {unassigned.length > 0 ? (
        <p className="text-sm text-text-muted">
          {unassigned.length} {unassigned.length === 1 ? "device has" : "devices have"} no
          authored block. Their membership is not recorded, not empty.
        </p>
      ) : null}
    </div>
  );
}
